/**
 * Visibility — agent-card profile fields, weights, and scoring (ADR-0113,
 * extended by ADR-0118 Slice A).
 *
 * The Visibility surface scores how complete a site's agent-readable profile is.
 * This module is the single source of truth for the field catalog + the score
 * function, shared by the origin (`GET /v1/sites/:id/visibility`) and the
 * dashboard so the number can't drift between server and UI.
 *
 * Pure data + pure functions only — no I/O, no LLM. Field *values* are filled
 * by the merchant or (Phase B, ADR-0113 / ADR-0040) by reviewed extraction;
 * this module just defines the shape and computes the weighted score.
 *
 * ADR-0118 Slice A adds four scored categories sourced from merchant-authoritative
 * structured tables (`site_compliance` + `catalog_product`) that are scored as
 * present/missing presence signals — not extracted by the LLM, not added to
 * `agent_card_profile`. Their IDs live in {@link StructuredFieldId} and are kept
 * disjoint from {@link ProfileFieldId} so the LLM closed-enum at
 * `@sill/profile-extraction` is unaffected (the suggestion pipeline still
 * targets exactly the 7 `agent_card_profile` fields it always has).
 *
 * snake_case wire naming per CLAUDE.md §3.
 */
/** Canonical field catalog + weights (ADR-0113 Q1: from the ADR-0110 mock). */
export const PROFILE_FIELDS = [
    { id: 'name', label: 'Business name', weight: 5 },
    { id: 'description', label: 'Description', weight: 5 },
    { id: 'price_range', label: 'Price range', weight: 4 },
    { id: 'currency', label: 'Currency', weight: 3 },
    { id: 'shipping_zones', label: 'Shipping zones', weight: 6 },
    { id: 'return_window_days', label: 'Return window (days)', weight: 8 },
    { id: 'subscription_billing', label: 'Subscription cadence', weight: 7 },
    { id: 'agent_card_authoritative', label: 'Authoritative agent card', weight: 4 },
];
/**
 * Structured-data scored categories + weights (ADR-0118 Slice A). Weights
 * fixed per the ADR scoring table; collectively 24 of the new 66-point
 * total so the structured-data signals are materially credited without
 * dwarfing the highest existing field (`return_window_days` at 8).
 */
export const STRUCTURED_FIELDS = [
    { id: 'returns_policy_set', label: 'Returns policy', weight: 6 },
    { id: 'seller_tos_url_set', label: 'Seller terms-of-service URL', weight: 5 },
    { id: 'seller_privacy_policy_url_set', label: 'Seller privacy-policy URL', weight: 5 },
    { id: 'catalog_search_eligible', label: 'Catalog (search-eligible product)', weight: 8 },
];
/** Weighted (present / total) → 0–100, rounded. Pure. */
export function computeVisibilityScore(fields) {
    const total = fields.reduce((sum, f) => sum + f.weight, 0);
    if (total === 0)
        return 0;
    const earned = fields.filter((f) => f.present).reduce((sum, f) => sum + f.weight, 0);
    return Math.round((earned / total) * 100);
}
/** A value counts as "present" only when it is a non-empty string. */
export function isFieldPresent(value) {
    return typeof value === 'string' && value.trim() !== '';
}
/**
 * Build a VisibilityReport from stored profile values using the canonical
 * catalog. Renders the {@link PROFILE_FIELDS} first (existing order) then
 * the {@link STRUCTURED_FIELDS} (ADR-0118 Slice A additions) — keeping the
 * dashboard's existing row order stable so a merchant returning after the
 * Slice A rollout sees the familiar list with four new rows appended.
 */
export function buildVisibilityReport(profile) {
    const fields = [
        ...PROFILE_FIELDS.map((def) => {
            const value = profile[def.id] ?? null;
            return { id: def.id, label: def.label, weight: def.weight, present: isFieldPresent(value), value };
        }),
        ...STRUCTURED_FIELDS.map((def) => {
            const value = profile[def.id] ?? null;
            return { id: def.id, label: def.label, weight: def.weight, present: isFieldPresent(value), value };
        }),
    ];
    return { score: computeVisibilityScore(fields), fields };
}
//# sourceMappingURL=visibility.js.map